/* eslint-disable */
import {
  EyeOutlined
} from '@ant-design/icons';
import {
  Button,
  Card,
  Space,
  Spin,
  Tooltip,
  Typography
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, {
  useEffect,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import Icon from 'common/components/Icon';
import PageTable from 'common/components/PageTable';
import { FilterDataProps, FilterValueProps, mappingFilterFields, mappingFilterToQuery, mappingParamsFilter, mappingQueryParamsFilter } from "common/components/PageTable/filter";
import { getAllApproveDepartmentSalaryService } from 'common/services/extends/approveSalary';
import { ApproveSalaryDataTypes } from 'common/services/extends/approveSalary/types';
import { getApproveStatusCountService } from 'common/services/extends/payrollPeriods';
import { handleStatusApprovedLabel, handleStatusLabel, levelInChargeLabel, ROUTE_PATHS } from 'common/utils/constant';
import { formatDateDDMMYYYY, formatNumber } from 'common/utils/functions';
import roles, { getPermission } from 'configs/roles';

export type TableDataTypes = {
} & ApproveSalaryDataTypes;

const ApproveSalaryManagement: React.FC<ActiveRoles> = ({
  roleUpdate,
  roleCreate,
  roleIndex
}) => {
  /* Hooks */
  const { t } = useTranslation();
  const navigate = useNavigate();

  /* Selectors */
  const {
    defaultPageSize, advancedFilter
  } = useAppSelector((state) => state.system);
  const {
    roles: rolesUser
  } = useAppSelector((state) => state.auth);

  /* Search Params */
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => {
    const paramsObj = { ...Object.fromEntries(searchParams.entries()) };
    if (searchParams.has('page')) delete paramsObj.page;
    return paramsObj;
  }, [searchParams]);
  const pageParam = searchParams.get('page');

  /* States */
  const [currentPage, setCurrentPage] = useState(Number(pageParam || 1));
  const [currentView, setCurrentView] = useState(defaultPageSize);
  const [keyword, setKeyword] = useState('');
  const [selectedFilterList, setSelectedFilterList] = useState<
    FilterValueProps[]>(mappingQueryParamsFilter(params));

  const returnParamsFilter = useMemo(
    () => mappingParamsFilter(selectedFilterList),
    [selectedFilterList]
  );

  const queryKey = ['approve-salary-list', keyword, currentPage, currentView, selectedFilterList];

  /* Queries */
  const { data: dataList, isLoading } = useQuery(
    queryKey,
    () => getAllApproveDepartmentSalaryService({
      ...(keyword && { keyword }),
      page: currentPage,
      limit: currentView,
      _payrollPeriodStatus: '2', // Đã tính lương
      _exceptPayrollPeriodStatusApprove: '1,3,4,5',
      ...returnParamsFilter
    }),
  );

  const { data: approveStatusCountRes, isLoading: approveStatusCountLoading } = useQuery(
    ['get-approve-status-count'],
    () => getApproveStatusCountService(),
    {
      enabled: roleIndex
    }
  );

  /* Variables */
  const columns: ColumnsType<TableDataTypes> = [
    {
      title: t('approveSalary.code'),
      dataIndex: 'code',
      key: 'code',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.projectData?.code}
        </Typography.Text>
      ),
    },
    {
      title: t('approveSalary.programName'),
      dataIndex: 'programName',
      key: 'programName',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.projectData?.name}
        </Typography.Text>
      ),
    },
    {
      title: t('approveSalary.customerName'),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 180,
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.projectData?.customerName}
        </Typography.Text>
      ),
    },
    {
      title: t('approveSalary.timeKeepingName'),
      dataIndex: 'timeKeepingName',
      key: 'timeKeepingName',
      width: 180,
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.payrollPeriodData?.name || ''}
        </Typography.Text>
      ),
    },
    {
      title: t('salary.totalIncome'),
      dataIndex: 'totalIncome',
      key: 'totalIncome',
      align: 'center',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {formatNumber(data?.payrollPeriodData?.totalIncome)}
        </Typography.Text>
      ),
    },
    {
      title: t('program.fromDate'),
      dataIndex: 'fromDate',
      key: 'fromDate',
      width: 120,
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {formatDateDDMMYYYY(data.payrollPeriodData?.startDate, '/')}
        </Typography.Text>
      ),
    },
    {
      title: t('program.toDate'),
      dataIndex: 'toDate',
      key: 'toDate',
      width: 120,
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {formatDateDDMMYYYY(data.payrollPeriodData?.endDate, '/')}
        </Typography.Text>
      ),
    },
    {
      title: t('system.personInCharge'),
      dataIndex: 'personInCharge',
      key: 'personInCharge',
      width: 120,
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {t(levelInChargeLabel(data.currentLevel) || '')}
        </Typography.Text>
      ),
    },
    {
      title: t('salary.approvedStatus'),
      dataIndex: 'approvedStatus',
      key: 'approvedStatus',
      width: 120,
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text className={`salary-statusApproved${` ${handleStatusApprovedLabel(data.payrollPeriodData?.statusApprove)}`}`}>
          {t(handleStatusLabel(data.payrollPeriodData?.statusApprove as number) || '')}
        </Typography.Text>
      ),
    },
    {
      title: t('approveSalary.creatorTableSalary'),
      dataIndex: 'creatorTableSalary',
      key: 'creatorTableSalary',
      width: 120,
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.payrollData?.fullname}
        </Typography.Text>
      ),
    },
    {
      title: t('program.personInChargeOP'),
      dataIndex: 'operation',
      key: 'operation',
      width: 120,
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.operation?.fullname || ''}
        </Typography.Text>
      ),
    },
    {
      title: t('program.personInChargeAC'),
      dataIndex: 'accountant',
      key: 'accountant',
      width: 120,
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.accountant?.fullname || ''}
        </Typography.Text>
      ),
    },
    {
      title: t('system.action'),
      key: 'action',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (_: string, data: TableDataTypes) => (
        <Space>
          <Tooltip title={t('system.viewDetail')}>
            <Button
              icon={<EyeOutlined />}
              disabled={!getPermission(rolesUser, roles.APPROVE_DEPARTMENT_SHOW)}
              onClick={() => navigate(`${ROUTE_PATHS.APPROVE_SALARY_DETAIL}?payrollPeriodId=${data.payrollPeriodData?.id}`)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleSetCurrentPage = (page: number) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set('page', page.toString());
    setCurrentPage(page);
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleSetCurrentView = (view: number) => {
    setCurrentView(view);
  };

  const handleFilter = (data: FilterValueProps) => {
    const typeFilter = String(data.filter).split('.')[1];
    if ((typeFilter === 'isNull' || typeFilter === 'isNotNull') && selectedFilterList.find((item) => item.key === data.key)) {
      return;
    }
    const counter = selectedFilterList.filter(
      (item) => item.field === data.field && item.filter === data.filter
    ).length;
    setSelectedFilterList([...selectedFilterList, { ...data, index: counter }]);
    handleSetCurrentPage(1);
  };

  const handleDeleteFilter = (key: string, index?: number) => {
    const tempList = selectedFilterList.slice();
    setSelectedFilterList(tempList.filter((item) => !(item.key === key && item.index === index)));
    handleSetCurrentPage(1);
  };

  const filterFields: FilterDataProps[] = useMemo(
    () => mappingFilterFields('payrollPeriodProcessing', advancedFilter),
    [advancedFilter]
  );

  useEffect(() => {
    setSearchParams({
      ...mappingFilterToQuery(selectedFilterList),
      ...pageParam && { page: pageParam }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilterList]);

  useEffect(() => {
    if (pageParam) {
      setCurrentPage(Number(pageParam));
    } else {
      setCurrentPage(1);
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set('page', '1');
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.waitingApproveSalary')}
      />
      <Spin spinning={isLoading || approveStatusCountLoading}>
        <div className="t-mainlayout_wrapper">
          {
            roleIndex && (
              <Card>
                <div className='p-approveSalary_waitingApprove'>
                  <div className='p-approveSalary_waitingApprove_left'>
                    <Typography.Title level={4}>
                      {t('approveSalary.waitingApprove')}
                    </Typography.Title>
                    <span>{approveStatusCountRes?.processing}</span>
                  </div>
                  <div className='p-approveSalary_waitingApprove_right'>
                    <Button onClick={() => navigate(ROUTE_PATHS.APPROVE_HISTORY)}>
                      <Space>
                        <Icon iconName='history' />
                        <span>
                          {t('approveSalary.history')}
                        </span>
                      </Space>
                    </Button>
                  </div>
                </div>
              </Card>
            )
          }
          <PageTable
            handleSearch={setKeyword}
            isLoading={isLoading}
            roles={{
              roleCreate,
              roleDelete: false,
              roleUpdate
            }}
            filtersDataTable={{
              handleFilter,
              selectedFilterList,
              handleDeleteFilter,
            }}
            noCheckbox
            tableProps={{
              initShowColumns: ['code', 'customerName', 'programName', 'timeKeepingName', 'totalIncome', 'creatorTableSalary', 'fromDate', 'toDate', 'personInCharge', 'accountant', 'operation', 'approvedStatus', 'action'],
              columns,
              pageData: dataList?.data || [],
              currentPage,
              pageSize: currentView,
              handleSetCurrentPage,
              handleSetCurrentView,
              total: dataList?.meta.total || 1,
              noBaseCol: true,
              noDeleteLanguage: true,
              filterFields,
              scroll: {
                x: 1800
              }
            }}
          />
        </div>
      </Spin>
    </>
  );
};

export default ApproveSalaryManagement;
